import {
  assertInstance,
  notNullish,
  parseBoolean,
  parseJsonObject,
  parseNumber,
  parseRecordAs,
  parseRecordValuesAs,
  parseString,
  parsingRecordAs,
  type TCoercer,
} from "@de-xima/xima-common-js-lang";
import { byCssAs, byCssHtml, byId } from "@de-xima/xima-common-js-dom";

import { byCssClass } from "../common/dom-util.ts";
import { findWidgetByElement, findWidgetByVar } from "../common/primefaces-util.ts";
import { hideLoadingDialog, showLoadingDialog } from "../components/loading-dialog.ts";
import { appendParameterToUrl } from "../common/form-util.ts";
import { startPollingDelayed } from "../common/polling-util.ts";
import { focusFirstFocusable } from "../common/accessibility.ts";

let verticalScroll = 0;

interface RedirectToFormResponse {
  statusCode: number;
  redirectUrl: string;
}

const TriggerResponseFrame = {
  name: "triggerResponseFrame",
  styleClass: "triggerResponseFrame",
  hideCssClass: "hideFrame",
  dialogWidgetVar: "triggerResponseDialog",
  responseFormIdSelector: "#triggerResponseForm",
  hiddenInputJsonResponse: "restApiJsonResponse",
};

const parseRedirectToFormResponse: TCoercer<RedirectToFormResponse> = parsingRecordAs({
  statusCode: parseNumber,
  redirectUrl: parseString,
});

export type TabNames = "MESSAGE" | "FILE" | "HISTORY";

export function blockEnterUnlessButton(e: KeyboardEvent): boolean {
  if (e.key === "Enter") {
    const target = e.target;
    if (
      target instanceof HTMLElement &&
      (target.tagName.toLowerCase() === "button" ||
        target.tagName.toLowerCase() === "a" ||
        (target.tagName.toLowerCase() === "input" && target instanceof HTMLInputElement && target.type === "button"))
    ) {
      return true;
    }

    e.preventDefault();
    return false;
  }
  return true;
}

/**
 * Adds all event listeners for the form record layout.
 */
export function initFormRecordsLayout(): void {
  const formRecordList = byCssClass("form-record__datatable");
  if (formRecordList) {
    startPollingDelayed("pollFormRecordListUpdate", 5000);
  }
  const triggerResponseIframe = byCssClass(TriggerResponseFrame.styleClass);
  if (triggerResponseIframe instanceof HTMLIFrameElement) {
    triggerResponseIframe.addEventListener("load", () => {
      if (!triggerResponseIframe.classList.contains(TriggerResponseFrame.hideCssClass)) {
        showTriggerRequestDialog();
        toggleFrameVisibility(false);
      }
    });
  }
}

export function startFormRecordPolling(): void {
  const formRecordList = byCssClass("form-record__tab-view");
  if (formRecordList) {
    startPollingDelayed("pollSelectedFormRecord", 5000);
  }
}

export function showFormRecordsLoadingIcon(): void {
  const loadingIcon = byCssClass("form-records-loading-wrapper");
  if (loadingIcon instanceof HTMLElement) {
    loadingIcon.style.display = "block";
  }
}

export function hideFormRecordsLoadingIcon(): void {
  const loadingIcon = byCssClass("form-records-loading-wrapper");
  if (loadingIcon instanceof HTMLElement) {
    loadingIcon.style.display = "none";
  }
}

export function receiveTriggerParameter(
  triggerUuid: string,
  taskUuid: string,
  formRecordUuid: string,
  triggerName: string,
): void {
  callSendWorkflowTriggerParameters(triggerUuid, taskUuid, formRecordUuid, triggerName);
}

/**
 * When the user clicks on a form record item in the list, this function is called.
 * @param event the click event.
 * @param tabName the name of the tab to open. Can be `MESSAGE`, `FILE` or `HISTORY`.
 */
export function onFormRecordItemClick(event: Event, tabName: TabNames): void {
  assertInstance(event.target, HTMLElement);
  const widget = event.target.closest(".ui-widget.ui-datascroller");
  const column = event.target.closest(".form-record__column");
  if (column instanceof HTMLElement && widget instanceof HTMLElement && widget.classList.contains("ui-datascroller")) {
    callBackToFormRecordSelectRemoteCommand(parseString(column.dataset.uuid), parseString(tabName), parseString(true));
  }
  verticalScroll = byId("content")?.scrollTop ?? 0;
}

/**
 * When the user clicks on a row of the form record list. Selects the form record
 * and shows details for the selected form record.
 * @param event the click event.
 */
export function onFormRecordRowClick(event: Event): void {
  assertInstance(event.target, HTMLElement);
  const widget = event.target.closest(".ui-widget");
  const column = event.target.closest(".form-record__column");
  if (column instanceof HTMLElement && widget instanceof HTMLElement && widget.classList.contains("ui-datascroller")) {
    callBackToFormRecordSelectRemoteCommand(parseString(column.dataset.uuid), parseString(""), parseString(true));
  }
  verticalScroll = byId("content")?.scrollTop ?? 0;
}

/**
 * Update the unread messages count in the form record page.
 */
export function updateUnreadCount(): void {
  const notificationHiddenInput = byCssAs("#headerForm\\:hidden-unread-count", HTMLInputElement);
  if (notificationHiddenInput === undefined) {
    return;
  }
  const notificationNumber = parseNumber(notificationHiddenInput.value);
  const notification = byCssHtml(".form-record-notification__count");
  const notificationMobile = byCssHtml(".form-record-notification__count--mobile");

  if (notification !== undefined) {
    if (notificationNumber === 0) {
      notification.remove();
      return;
    }
    notification.innerText = notificationHiddenInput.value;
  } else {
    if (notificationNumber > 0) {
      const option2 = byCssHtml("#formRecordsForm\\:formRecordFilter\\:1");
      if (option2 !== undefined) {
        const span = document.createElement("span");
        span.className = "form-record-notification__count";
        span.textContent = notificationHiddenInput.value;
        span.setAttribute("aria-hidden", "true");
        option2?.after(span);
      }
    }
  }

  if (notificationMobile !== undefined) {
    if (notificationNumber === 0) {
      notificationMobile.remove();
      return;
    }
    notificationMobile.innerText = notificationHiddenInput.value;
  } else {
    if (notificationNumber > 0) {
      const optionMobile = document.querySelector(
        ".form-record-filter__panel-select.ui-selectcheckboxmenu-panel .ui-selectcheckboxmenu-list .ui-selectcheckboxmenu-item:nth-child(2)",
      );
      if (optionMobile !== undefined) {
        const spanMobile = document.createElement("span");
        spanMobile.className = "form-record-notification__count--mobile";
        spanMobile.textContent = notificationHiddenInput.value;
        spanMobile.setAttribute("aria-hidden", "true");
        optionMobile?.appendChild(spanMobile);
      }
    }
  }
}

/**
 * Restores the vertical scroll position of the form record list. When the user select a form
 * record, its details are shown. When the user click on the back button, the view switches
 * back to the form record list. This function restores the vertical scroll position of the list
 * from before the user clicked on the form record.
 */
export function restoreVerticalScroll(): void {
  const scrollContainer = byId("content");
  if (scrollContainer !== undefined) {
    scrollContainer.scrollTop = verticalScroll;
  }
}

export function markAsRead(uuid: string): void {
  if (uuid) {
    const entry = byCssClass(`row-${uuid}`);
    if (entry !== undefined) {
      entry.classList.remove("entry--new");
    }
  }
}

/**
 * Execute the trigger request.
 * @param args The PrimeFaces arguments object with the data received from the
 * bean.
 */
export async function executeTrigger(args: PrimeFaces.ajax.PrimeFacesArgs): Promise<void> {
  showLoadingDialog();
  const data = parseJsonObject(args.triggerRequestData);
  const url = parseString(data.triggerRequestUrl);
  const cookieName = parseString(data.cookieName);
  const contextPath = parseString(data.contextPath);
  const triggerName = parseString(data.triggerName);
  const apiUrl = parseString(data.apiUrl);
  const formRecordUuid = parseString(data.formRecordUuid);
  const authHeader = parseRecordValuesAs(data.authHeader, parseString);
  const portalAlias = parseString(data.portalAlias);

  try {
    const response = await fetch(apiUrl, {
      method: "POST",
      headers: authHeader,
    });
    const jsonResponse = (await response.json()) as unknown;
    const form = document.querySelector(TriggerResponseFrame.responseFormIdSelector);

    if (form instanceof HTMLFormElement) {
      prepareFormForSubmission(form, url, jsonResponse, cookieName, contextPath);
      await waitForTriggerResponseCompletionCookie(cookieName, contextPath);
      checkRedirectToForm(jsonResponse, portalAlias);
    }
    await callUpdateFormRecordRowRemoteCommand(formRecordUuid);
    callSuccessGrowlRemoteCommand(triggerName);
  } catch (error) {
    console.error("Error executing POST request:", error);
    hideTriggerRequestDialog();
    callErrorGrowlRemoteCommand(triggerName);
  } finally {
    hideLoadingDialog();
  }
}

/**
 * Open the split button menu when the user click on the left part of a split button.
 * Normally, the default action is executed, and the menu with alternative actions
 * only opens when the user clicks on the down arrow on the right side of the button.
 *
 * We want the menu to open irrespective of where the user clicks on the button.
 * @param event The event from opening the split button menu.
 */
export function openSplitButtonMenu(event: Event): void {
  if (event instanceof MouseEvent === false) {
    return;
  }
  assertInstance(event.target, HTMLElement);
  const splitButton = findWidgetByElement(event.target, PrimeFaces.widget.SplitButton);
  if (splitButton) {
    const button = event.target.closest(".ui-button");
    const menuButton = button?.parentElement?.querySelector(".ui-splitbutton-menubutton");
    if (button && menuButton && Array.from(button.parentElement?.children ?? []).indexOf(menuButton) !== -1) {
      splitButton?.menuButton.trigger("click");
      return;
    }
  }
}

export function focusFirstWorkflowTriggerButton(container: HTMLElement): void {
  focusFirstFocusable(container);
}

/**
 * Updates the marked form record row. Only one row can be marked at a time.
 * Unmarks all form record rows and marks the row with the given UUID.
 * @param uuid The UUID of the form record row to mark.
 */
export function updateMarkedFormRecordRow(uuid: string): void {
  const dataScroller = findWidgetByVar("formRecords", PrimeFaces.widget.DataScroller);
  if (dataScroller === undefined) {
    console.error("DataScroller widget not found.");
    return;
  }
  const columns = dataScroller.list.find(".form-record__column").toArray();
  for (const column of columns) {
    column.classList.remove("form-record__column--marked");
  }
  columns.filter((x) => x.dataset.uuid === uuid)[0]?.classList.add("form-record__column--marked");
}

/**
 * Pushes the given URL to the history state, allowing the user to navigate back to this state.
 * @param url The URL to select the form record link.
 */
export function pushState(url: string): void {
  history.pushState({}, "", url);
}

/**
 * Selects the form record link by pushing the URL to the history state.
 * @param url The URL to select the form record link.
 */
export function selectFormRecordLink(url: string): void {
  pushState(url);
}

/**
 * Adds a popstate event listener to the window to handle navigation.
 */
export function addFormRecordPopstateListener(): void {
  window.addEventListener("popstate", () => {
    const currentUrl = window.location.href;
    if (currentUrl.includes("/form-records")) {
      restoreVerticalScroll();
      callBackToFormRecordsRemoteCommand();
    }
    if (currentUrl.includes("/form-record/")) {
      const formRecordUuid = extractUuidFromUrl(currentUrl);
      callBackToFormRecordSelectRemoteCommand(formRecordUuid, parseString(0), parseString(false));
    }
  });
}

/**
 * Extracts the UUID from a URL that contains a form record link.
 * @param url The URL to extract the UUID from.
 * @returns The extracted UUID or empty if the URL does not contain a form record link.
 */
function extractUuidFromUrl(url: string): string {
  const marker = "form-record/";
  const index = url.indexOf(marker);
  if (index === -1) {
    return "";
  }
  return url.substring(index + marker.length);
}

/**
 * Prepare the form for submission by setting its attributes and adding the JSON response as a hidden input.
 * @param form The form element.
 * @param url The URL to set as the form action.
 * @param jsonResponse The JSON response.
 * @param cookieName The name of the cookie to delete.
 * @param contextPath The context path for the cookie.
 */
function prepareFormForSubmission(
  form: HTMLFormElement,
  url: string,
  jsonResponse: unknown,
  cookieName: string,
  contextPath: string,
): void {
  form.action = url;
  form.method = "POST";
  form.target = TriggerResponseFrame.name;

  checkTriggerResponseFrameVisibility(jsonResponse);

  PrimeFaces.deleteCookie(cookieName, { path: contextPath });

  const existingInput = form.querySelector(`#${TriggerResponseFrame.hiddenInputJsonResponse}`);
  if (existingInput) {
    form.removeChild(existingInput);
  }

  const input = document.createElement("input");
  input.type = "hidden";
  input.id = TriggerResponseFrame.hiddenInputJsonResponse;
  input.name = TriggerResponseFrame.hiddenInputJsonResponse;
  input.value = JSON.stringify(jsonResponse);
  form.appendChild(input);

  form.submit();
}

/**
 * Checks the visibility of the trigger response frame based on the JSON response.
 * The frame is hidden if the response status code is 204 or if the forceDownload flag is set to true
 * @param jsonResponse The JSON response object.
 */
function checkTriggerResponseFrameVisibility(jsonResponse: unknown): void {
  const parsed = parseRecordAs(jsonResponse, {
    statusCode: parseNumber,
    forceDownload: parseBoolean,
  });
  const shouldShowFrame = notNullish(jsonResponse) && parsed.statusCode !== 204 && !parsed.forceDownload;
  toggleFrameVisibility(shouldShowFrame);
}

function showTriggerRequestDialog(): void {
  const dialog = PF(TriggerResponseFrame.dialogWidgetVar);
  if (dialog instanceof PrimeFaces.widget.Dialog) {
    dialog.show();
  }
}

function hideTriggerRequestDialog(): void {
  const dialog = PF(TriggerResponseFrame.dialogWidgetVar);
  if (dialog instanceof PrimeFaces.widget.Dialog) {
    dialog.hide();
  }
}

/**
 *  Wait for the trigger response completion cookie to be set. The cookie is set by the server when the response is ready.
 * @param cookieName The name of the cookie to wait for.
 * @param path The path of the cookie.
 * @returns A promise that resolves when the cookie is set.
 */
function waitForTriggerResponseCompletionCookie(cookieName: string, path: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const interval = setInterval(() => {
      if (PrimeFaces.getCookie(cookieName) === "true") {
        clearInterval(interval);
        clearTimeout(timeout);
        PrimeFaces.deleteCookie(cookieName, { path });
        resolve();
      }
    }, 100);
    const timeout = setTimeout(
      () => {
        clearInterval(interval);
        clearTimeout(timeout);
        reject(new Error("Timeout waiting for response cookie"));
      },
      10 * 60 * 1000,
    );
  });
}

/**
 * Toggles the visibility of the trigger response iframe.
 * @param showFrame If true, the frame will be shown, otherwise it will be hidden.
 */
function toggleFrameVisibility(showFrame: boolean): void {
  const triggerResponseIframe = byCssClass(TriggerResponseFrame.styleClass);
  if (triggerResponseIframe instanceof HTMLIFrameElement) {
    if (showFrame) {
      triggerResponseIframe.classList.remove(TriggerResponseFrame.hideCssClass);
    } else {
      triggerResponseIframe.classList.add(TriggerResponseFrame.hideCssClass);
    }
  }
}

/**
 * Checks if the JSON response contains a redirect URL to a form and submits the form in an IFRAME.
 * @param jsonResponse
 * @param portalAlias
 */
function checkRedirectToForm(jsonResponse: unknown, portalAlias: string): void {
  if (!jsonResponse) {
    return;
  }
  const parsed = parseRedirectToFormResponse(jsonResponse);
  if (parsed.statusCode !== 302 || !parsed.redirectUrl) {
    return;
  }
  const redirectUrl = parsed.redirectUrl;
  const isForm = isFcForm(redirectUrl);
  if (isForm) {
    submitFormInIframe(redirectUrl, portalAlias);
  }
}

/**
 * Checks if the redirect URL is from the same domain and contains /form/provide/.
 * @param redirectUrl The URL to check.
 * @returns True if the URL is from the same domain and contains /form/provide/, false otherwise.
 */
function isFcForm(redirectUrl: string): boolean {
  try {
    const currentHostname = window.location.hostname;
    const redirectHostname = new URL(redirectUrl).hostname;
    return currentHostname === redirectHostname && redirectUrl.includes("/form/provide/");
  } catch (e) {
    console.error("Error parsing redirect URL:", e);
    return false;
  }
}

/**
 * Submits a form with a POST request to the given URL within the IFRAME.
 * @param url The URL to submit the form to.
 * @param portalAlias The portal alias to use.
 */
function submitFormInIframe(url: string, portalAlias: string) {
  const iframe = document.querySelector(`iframe[name="${TriggerResponseFrame.name}"]`);
  if (iframe instanceof HTMLIFrameElement && iframe.contentDocument) {
    const form = document.createElement("form");

    form.action = appendParameterToUrl(url, "portal_alias", portalAlias);
    form.method = "POST";

    iframe.contentDocument.body.appendChild(form);
    form.submit();
  }
}

/**
 * Updates the form record row with the given UUID.
 * @param formRecordUuid The UUID of the form record.
 */
export async function updateFormRecordRow(formRecordUuid: string): Promise<void> {
  await callUpdateFormRecordRowRemoteCommand(formRecordUuid);
}

/**
 * Calls the remote command to update the form record row.
 * @param formRecordUuid The UUID of the form record.
 */
async function callUpdateFormRecordRowRemoteCommand(formRecordUuid: string): Promise<void> {
  await rcUpdateFormRecordRow?.([{ name: "formRecordUuid", value: formRecordUuid }]);
}

/**
 * When the user clicks on the back button, this function is called to switch back to the form records list.
 */
function callBackToFormRecordsRemoteCommand(): void {
  rcBackToFormRecords?.([]);
}

/**
 * When the user selects a form record, this function is called to select the form record
 * @param formRecordUuid The UUID of the form record to select
 * @param tabName The name of the tab to open. Can be "MESSAGE", "FILE" or "HISTORY".
 * @param pushState If true, the state will be pushed to the history, otherwise the state will not be changed
 */
function callBackToFormRecordSelectRemoteCommand(formRecordUuid: string, tabName: string, pushState: string): void {
  rcFormRecordRowSelect?.([
    { name: "formRecordUuid", value: formRecordUuid },
    { name: "tabName", value: tabName },
    { name: "pushState", value: pushState },
  ]);
}

function callSuccessGrowlRemoteCommand(triggerName: string): void {
  rcSendWorkflowTriggerSuccess?.([{ name: "triggerName", value: triggerName }]);
}

function callErrorGrowlRemoteCommand(triggerName: string): void {
  rcSendWorkflowTriggerError?.([{ name: "triggerName", value: triggerName }]);
}

function callSendWorkflowTriggerParameters(
  triggerUuid: string,
  taskUuid: string,
  formRecordUuid: string,
  triggerName: string,
): void {
  rcSendWorkflowTriggerParameter?.([
    { name: "triggerName", value: triggerName },
    {
      name: "triggerUuid",
      value: triggerUuid,
    },
    { name: "taskUuid", value: taskUuid },
    { name: "formRecordUuid", value: formRecordUuid },
  ]);
}
